import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  Grid,
  LinearProgress,
  Chip,
  IconButton,
  TextField,
  FormControlLabel,
  Switch,
  Divider,
  Alert,
  useTheme,
  useMediaQuery,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Fade,
  CircularProgress
} from '@mui/material';
import {
  CloudUpload,
  Description,
  Download,
  CheckCircle,
  Add,
  Assignment,
  Settings,
  Piano,
  Edit,
  OpenInNew,
  Refresh,
  DragIndicator,
  Delete
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axiosinstance from '../../utils/axiosConfig';

interface TechnicalPhaseProps {
  resource: any;
  onUpdate: (updatedResource: any) => void;
}

interface ContractItem {
  _id: string;
  contractNumber: string;
  artistName: string;
  status: 'draft' | 'pending_signature' | 'signed' | 'cancelled';
  documentUrl?: string;
  eventDate: string;
  venue: string;
  createdAt: string;
}

interface FileItem {
  name: string;
  url: string;
  uploadedAt: string;
  size?: number;
  type: string;
}

const TechnicalPhase: React.FC<TechnicalPhaseProps> = ({ resource, onUpdate }) => {
  const [contracts, setContracts] = useState<ContractItem[]>([]);
  const [schedules, setSchedules] = useState<FileItem[]>([]);
  const [technicalDocs, setTechnicalDocs] = useState<FileItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState<Record<string, boolean>>({});
  const [scheduleItems, setScheduleItems] = useState<any[]>([]);
  const [signedContracts, setSignedContracts] = useState<FileItem[]>([]);
  const [fileToDelete, setFileToDelete] = useState<{
  file: FileItem;
  category: 'schedule' | 'technical' | 'signed_contract';
} | null>(null);

  // Enhanced upload progress tracking
  const [uploadProgress, setUploadProgress] = useState<Record<string, {
    uploading: boolean;
    progress: number;
    message: string;
  }>>({});

  // Drag and drop states
  const [dragOver, setDragOver] = useState<string | null>(null);
  
  // Snackbar for better user feedback
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });
  
  // Technical requirements state
  const [pianoTuning, setPianoTuning] = useState({
    requirements: resource?.pianoTuningRequirements || '',
    status: resource?.pianoTuningStatus || 'not_required'
  });
  const [engineerRequirements, setEngineerRequirements] = useState(
    resource?.engineerRequirements || ''
  );
  const [includesChannelList, setIncludesChannelList] = useState(
    resource?.includesChannelList || false
  );

  // Dialog state
  const [uploadDialog, setUploadDialog] = useState(false);
  const [uploadType, setUploadType] = useState<'schedule' | 'technical' | 'signed_contract' | null>(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const showNotification = (message: string, severity: 'success' | 'error' | 'info' | 'warning' = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const updateUploadProgress = (key: string, progress: number, message: string) => {
    setUploadProgress(prev => ({
      ...prev,
      [key]: { uploading: true, progress, message }
    }));
  };

  const clearUploadProgress = (key: string) => {
    setTimeout(() => {
      setUploadProgress(prev => {
        const newState = { ...prev };
        delete newState[key];
        return newState;
      });
    }, 3000);
  };

  useEffect(() => {
    if (resource?._id) {
      fetchContracts();
      fetchSchedules(); // Fetch schedules first
    }
  }, [resource]);

  // Fetch files after schedules are loaded
  useEffect(() => {
    if (resource?._id && scheduleItems.length >= 0) {
      fetchFiles(); 
    }
  }, [resource, scheduleItems]);

  const fetchContracts = async () => {
    setLoading(true);
    try {
      // Get performance info for matching
      const performanceId = resource.performances?.[0]?._id || resource.performances?.[0];
      const resourceName = resource.name || '';
      const artistName = resourceName.replace(' resource pack', '').replace(' Resource Pack', '').replace(' - Resources', '').trim();

      console.log('Looking for contracts matching:', {
        performanceId: performanceId,
        artistName: artistName
      });

      // Get all contracts from database (single source of truth)
      const contractsResponse = await axiosinstance.get('/api/contracts');
      
      // Filter contracts that match this performance/artist
      const matchingContracts = contractsResponse.data.filter((contract: any) => {
        const contractPerformanceId = contract.contractData?.performanceId;
        const contractArtistName = contract.artistName?.toLowerCase() || '';
        const searchArtistName = artistName.toLowerCase();

        const matchesPerformance = contractPerformanceId === performanceId;
        const matchesArtist = contractArtistName.includes(searchArtistName) || 
                             searchArtistName.includes(contractArtistName);

        return matchesPerformance || matchesArtist;
      });

      console.log('Found matching contracts:', matchingContracts);
      setContracts(matchingContracts);

    } catch (error) {
      console.error('Error fetching contracts:', error);
      setContracts([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSchedules = async () => {
    try {
      const performanceId = resource.performances?.[0]?._id || resource.performances?.[0];
      
      if (!performanceId) {
        console.log('No performance ID found for schedule lookup');
        return;
      }

      // Get schedule for this specific performance using existing route
      const response = await axiosinstance.get(`/api/schedules/by-performance/${performanceId}`);
      
      // The route returns a single schedule, not an array
      console.log('Found schedule:', response.data);
      setScheduleItems([response.data]);

    } catch (error) {
      if (
        typeof error === 'object' &&
        error !== null &&
        'response' in error &&
        typeof (error as any).response === 'object' &&
        (error as any).response !== null &&
        'status' in (error as any).response &&
        (error as any).response.status === 404
      ) {
        // No schedule found - this is normal, not an error
        console.log('No schedule found for this performance');
        setScheduleItems([]);
      } else {
        console.error('Error fetching schedules:', error);
        setScheduleItems([]);
      }
    }
  };

// Fixed fetchFiles function for your TechnicalPhase.tsx
// Replace your current fetchFiles function with this one

const fetchFiles = async () => {
  try {
    const response = await axiosinstance.get(`/api/resources/${resource._id}/files`);
    const data = response.data || {};

    const scheduleFiles: FileItem[] = data.schedules ? Object.values(data.schedules) : [];

    // Flatten all files from technical
    const technicalRaw: FileItem[] = data.technical
      ? Object.values(data.technical).flatMap((item) =>
          Array.isArray(item) ? item : [item]
        )
      : [];

    // Split by subtype
    const signedContractFiles = technicalRaw.filter((file) =>
      file.type === 'signed_contract' || file.name.toLowerCase().includes('signed')
    );

    const otherTechnicalDocs = technicalRaw.filter(
      (file) => !signedContractFiles.includes(file)
    );

    setSchedules(scheduleFiles);
    setTechnicalDocs(otherTechnicalDocs);
    setSignedContracts(signedContractFiles);
  } catch (error) {
    console.error('Error fetching files:', error);
  }
};
  const refreshFiles = async () => {
    await fetchFiles();
    showNotification('Files refreshed successfully!', 'success');
  };

  const handleCreateContract = async () => {
    try {
      const performanceId = resource.performances?.[0]?._id || resource.performances?.[0];
      
      if (!performanceId) {
        alert('No performance associated with this resource');
        return;
      }

      // Navigate to contract creation with context
      navigate('/admin/contracts/create', {
        state: {
          performanceId: performanceId,
          resourceId: resource._id,
          returnTo: `/admin/resources/${resource._id}?tab=technical`
        }
      });

    } catch (error) {
      console.error('Error creating contract:', error);
      alert('Failed to create contract');
    }
  };

  const handleCreateSchedule = async () => {
    try {
      const performanceId = resource.performances?.[0]?._id || resource.performances?.[0];
      
      if (!performanceId) {
        alert('No performance associated with this resource');
        return;
      }

      // Navigate to schedule creation with context
      navigate('/admin/schedules/create', {
        state: {
          performanceId: performanceId,
          resourceId: resource._id,
          returnTo: `/admin/resources/${resource._id}?tab=technical`
        }
      });

    } catch (error) {
      console.error('Error creating schedule:', error);
      alert('Failed to create schedule');
    }
  };

  const downloadContract = async (contract: ContractItem) => {
    try {
      // Extract timestamp and create filename
      const createdDate = new Date(contract.createdAt);
      const timestamp = contract.documentUrl?.split('_')[0];
      
      if (!timestamp) {
        console.warn('No timestamp in documentUrl, generating one:', contract.documentUrl);
        const generatedTimestamp = createdDate.getTime().toString();
        
        // Try fallback filename
        const fileName = `${generatedTimestamp}_${contract.contractNumber}_${contract.artistName}_contract.docx`;
        const documentUrl = `contracts/${fileName}`;
        
        const { contractServices } = await import('../../services/contractServices');
        const blob = await contractServices.downloadContract(documentUrl);
        
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        return;
      }
      
      // Use existing timestamp or create new one
      const fileTimestamp = timestamp || Date.now().toString();
      
      // Construct filename based on actual bucket structure (same as main contracts page)
      const fileName = `${fileTimestamp}_${contract.contractNumber}_${contract.artistName}_contract.docx`;
      const documentUrl = `contracts/${fileName}`;

      // Use contractServices method that works in main page
      const { contractServices } = await import('../../services/contractServices');
      const blob = await contractServices.downloadContract(documentUrl);
      
      // Create download link (same as main page)
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Download failed:', error);
      alert('Download failed');
    }
  };

  // Enhanced file upload with better progress and drag/drop
  const handleFileUpload = async (file: File, category: 'schedule' | 'technical' | 'signed_contract') => {
    const uploadKey = `${category}_upload`;
    
    setUploading(prev => ({ ...prev, [uploadKey]: true }));
    updateUploadProgress(uploadKey, 10, 'Preparing upload...');

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('fileType', category);
      formData.append('category', category === 'signed_contract' ? 'technical' : category === 'schedule' ? 'schedules' : 'technical');
      formData.append('performanceId', resource.performances?.[0]?._id || '');
      
      if (category === 'signed_contract') {
        formData.append('subtype', 'signed_contract');
      }

      updateUploadProgress(uploadKey, 30, 'Uploading file...');

      const response = await axiosinstance.post(
        `/api/resources/${resource._id}/upload`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              const adjustedProgress = 30 + (progress * 0.5); // 30-80%
              updateUploadProgress(uploadKey, adjustedProgress, `Uploading... ${progress}%`);
            }
          }
        }
      );

      updateUploadProgress(uploadKey, 90, 'Processing upload...');

      console.log('File uploaded:', response.data);
      
      // Refresh files immediately
      await fetchFiles();
      
      updateUploadProgress(uploadKey, 100, 'Upload completed!');
      
      showNotification(`${category === 'signed_contract' ? 'Signed contract' : category} uploaded successfully!`, 'success');
      setUploadDialog(false);

      clearUploadProgress(uploadKey);

    } catch (error) {
      console.error('Upload failed:', error);
      showNotification('Upload failed. Please try again.', 'error');
      setUploadProgress(prev => {
        const newState = { ...prev };
        delete newState[uploadKey];
        return newState;
      });
    } finally {
      setUploading(prev => ({ ...prev, [uploadKey]: false }));
    }
  };

  // Drag and drop handlers
  const handleDragEnter = (e: React.DragEvent, category: string) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(category);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only clear if we're leaving the actual drop zone
    if (!(e.currentTarget as Element).contains(e.relatedTarget as Node)) {
      setDragOver(null);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent, category: 'schedule' | 'technical' | 'signed_contract') => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(null);

    const droppedFiles = Array.from(e.dataTransfer.files);
    const file = droppedFiles[0];

    if (file) {
      const validTypes = ['.pdf', '.doc', '.docx'];
      const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
      
      if (validTypes.includes(fileExtension)) {
        handleFileUpload(file, category);
      } else {
        showNotification('Please upload PDF, DOC, or DOCX files only.', 'error');
      }
    }
  };

  const updateTechnicalRequirements = async () => {
    try {
      const updates = {
        pianoTuningRequirements: pianoTuning.requirements,
        pianoTuningStatus: pianoTuning.status,
        engineerRequirements,
        includesChannelList
      };

      const response = await axiosinstance.put(`/api/resources/${resource._id}`, updates);
      onUpdate(response.data);
      showNotification('Technical requirements saved successfully!', 'success');

    } catch (error) {
      console.error('Failed to update technical requirements:', error);
      showNotification('Failed to save requirements', 'error');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'signed': return 'success';
      case 'pending_signature': return 'warning';
      case 'draft': return 'info';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    return status.replace('_', ' ').toUpperCase();
  };

  const ContractCard = ({ contract }: { contract: ContractItem }) => (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box sx={{ flexGrow: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Typography variant="h6">
                {contract.contractNumber}
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {contract.artistName} • {contract.venue}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {new Date(contract.eventDate).toLocaleDateString()}
            </Typography>
            <Chip 
              label={getStatusLabel(contract.status)} 
              color={getStatusColor(contract.status)}
              size="small"
            />
          </Box>
          
          <Stack direction="row" spacing={1}>
            <Tooltip title="Download Contract">
              <IconButton 
                size="small"
                onClick={() => downloadContract(contract)}
              >
                <Download />
              </IconButton>
            </Tooltip>
            
            <Tooltip title="Edit Contract">
              <IconButton 
                size="small"
                onClick={() => navigate(`/admin/contracts/edit/${contract._id}`)}
              >
                <Edit />
              </IconButton>
            </Tooltip>

            <Tooltip title="View Contract Details">
              <IconButton 
                size="small"
                onClick={() => window.open(`/admin/contracts/${contract._id}`, '_blank')}
              >
                <OpenInNew />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );

  const FileUploadZone = ({ 
    title, 
    description, 
    files,
    uploadKey 
  }: { 
    title: string; 
    description: string; 
    files: FileItem[];
    uploadKey: 'schedule' | 'technical' | 'signed_contract';
  }) => {
    const currentProgress = uploadProgress[`${uploadKey}_upload`];
    const isDragging = dragOver === uploadKey;

    
    return (
      <Card 
  variant="outlined"
  sx={{
    border: isDragging ? '2px dashed' : '1px solid',
    borderColor: isDragging
      ? 'primary.main'
      : files.length > 0
        ? 'success.main' // ✅ green outline when files exist
        : 'divider',
    transition: 'all 0.2s ease-in-out',
    position: 'relative'
        }}
        onDragEnter={(e) => handleDragEnter(e, uploadKey)}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={(e) => handleDrop(e, uploadKey)}
      >
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6">{title}</Typography>
            <Tooltip title="Refresh files">
              <IconButton size="small" onClick={refreshFiles}>
                <Refresh />
              </IconButton>
            </Tooltip>
          </Stack>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {description}
          </Typography>

          {/* Upload Progress */}
          {currentProgress?.uploading && (
            <Fade in={true}>
              <Box sx={{ mb: 2 }}>
                <LinearProgress 
                  variant="determinate" 
                  value={currentProgress.progress} 
                  sx={{ mb: 1 }}
                />
                <Typography variant="body2" color="text.secondary" align="center">
                  {currentProgress.message}
                </Typography>
              </Box>
            </Fade>
          )}

          {/* Drag Over Indicator */}
          {isDragging && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'primary.main',
                opacity: 0.1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1,
                borderRadius: 1
              }}
            >
              <Typography variant="h6" color="primary">
                Drop {title} here
              </Typography>
            </Box>
          )}

          {files.length > 0 ? (
            <List dense>
              {files.map((file, index) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    <Description />
                  </ListItemIcon>
                  <ListItemText 
                    primary={file.name}
                    secondary={new Date(file.uploadedAt).toLocaleDateString()}
                  />
<ListItemSecondaryAction>
  <Tooltip title="Download">
    <IconButton
      size="small"
      onClick={() => window.open(file.url, '_blank')}
    >
      <Download />
    </IconButton>
  </Tooltip>
  <Tooltip title="Delete">
    <IconButton
      size="small"
      onClick={() => setFileToDelete({ file, category: uploadKey })}
    >
      <Delete />
    </IconButton>
  </Tooltip>
</ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          ) : (
            <Alert severity="info" sx={{ mb: 2 }}>
              {isDragging ? `Drop ${title.toLowerCase()} here` : `No ${title.toLowerCase()} uploaded yet`}
            </Alert>
          )}

          <Button
            variant={isDragging ? "contained" : "outlined"}
            startIcon={
              currentProgress?.uploading ? 
                <CircularProgress size={20} /> : 
                <CloudUpload />
            }
            onClick={() => {
              setUploadType(uploadKey);
              setUploadDialog(true);
            }}
            disabled={currentProgress?.uploading}
            fullWidth
            sx={{
              transition: 'all 0.2s ease-in-out'
            }}
          >
            {currentProgress?.uploading ? 'Uploading...' : isDragging ? `Drop ${title}` : `Upload ${title}`}
          </Button>

          {!currentProgress?.uploading && (
            <Typography 
              variant="caption" 
              color="text.secondary" 
              display="block" 
              sx={{ mt: 1, textAlign: 'center' }}
            >
              Or drag and drop files here
            </Typography>
          )}
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2 }}>Loading technical information...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: isMobile ? 2 : 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h6">
          Technical Requirements & Documentation
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={refreshFiles}
          size="small"
        >
          Refresh All
        </Button>
      </Stack>

      <Grid container spacing={3}>
        {/* Contracts Section */}
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Assignment />
            Contracts
          </Typography>

          <Grid container spacing={2}>
            {/* Generated Contracts */}
            <Grid item xs={12} md={8}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Generated Contracts
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    All contract versions from database
                  </Typography>

                  {contracts.length > 0 ? (
                    contracts.map((contract) => (
                      <ContractCard key={contract._id} contract={contract} />
                    ))
                  ) : (
                    <>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        No contract found for this performance. Create one using the contract workflow.
                      </Alert>
                      <Button 
                        variant="outlined" 
                        startIcon={<Add />}
                        onClick={handleCreateContract}
                        fullWidth
                      >
                        Create Contract
                      </Button>
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Signed Contract Upload */}
            <Grid item xs={12} md={4}>
  <FileUploadZone
    title="Signed Contract"
    description="Upload signed contract documents"
    files={signedContracts} // ✅ Fixed
    uploadKey="signed_contract"
  />
</Grid>
          </Grid>
        </Grid>

        {/* Schedule Section */}
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Description />
            Schedules
          </Typography>

          <Grid container spacing={2}>
            {/* Generated Schedules */}
            <Grid item xs={12} md={8}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Generated Schedules
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    All schedule versions from database
                  </Typography>

                  {scheduleItems.length > 0 ? (
                    scheduleItems.map((schedule) => (
                      <Card key={schedule._id} variant="outlined" sx={{ mb: 2 }}>
                        <CardContent>
                          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                            <Box sx={{ flexGrow: 1 }}>
                              <Typography variant="h6" gutterBottom>
                                Schedule v{schedule.version}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" gutterBottom>
                                Created {new Date(schedule.createdAt).toLocaleDateString()}
                              </Typography>
                              <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                                <Chip 
                                  label={`${schedule.scheduleData?.events?.length || 0} event${schedule.scheduleData?.events?.length !== 1 ? 's' : ''}`}
                                  size="small"
                                  color="primary"
                                />
                                {schedule.pdfUrl && (
                                  <Chip 
                                    label="PDF Generated"
                                    size="small"
                                    color="success"
                                  />
                                )}
                                {schedule.scheduleData?.isInternational && (
                                  <Chip 
                                    label="International"
                                    size="small"
                                    color="info"
                                  />
                                )}
                              </Stack>
                            </Box>
                            <Stack direction="row" spacing={1}>
                              {schedule.pdfUrl && (
                                <Tooltip title="Download PDF">
                                  <IconButton 
                                    size="small"
                                    onClick={() => window.open(schedule.pdfUrl, '_blank')}
                                  >
                                    <Download />
                                  </IconButton>
                                </Tooltip>
                              )}
                              <Tooltip title="Edit Schedule">
                                <IconButton 
                                  size="small"
                                  onClick={() => navigate('/admin/performances/schedule', { 
                                    state: { 
                                      scheduleId: schedule._id,
                                      resourceId: schedule.resourceId
                                    }
                                  })}
                                >
                                  <Edit />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </Stack>
                          {schedule.lastPdfGenerated && (
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                              PDF last generated: {new Date(schedule.lastPdfGenerated).toLocaleString()}
                            </Typography>
                          )}
                        </CardContent>
                      </Card>
                    ))
                  ) : (
                    <>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        No schedule found for this performance. Create one using the schedule workflow.
                      </Alert>
                      <Button 
                        variant="outlined" 
                        startIcon={<Add />}
                        onClick={handleCreateSchedule}
                        fullWidth
                      >
                        Create Schedule
                      </Button>
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Schedule Upload */}
            <Grid item xs={12} md={4}>
              <FileUploadZone
                title="Schedule PDF"
                description="Upload schedule documents"
                files={schedules}
                uploadKey="schedule"
              />
            </Grid>
          </Grid>
        </Grid>

        {/* Technical Documents */}
        <Grid item xs={12} md={6}>
          <FileUploadZone
            title="Technical Documents"
            description="Upload technical specifications, rider requirements, etc."
            files={technicalDocs}
            uploadKey="technical"
          />
        </Grid>

        {/* Technical Requirements */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Technical Requirements
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" gutterBottom>
                    <Piano sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Piano Tuning
                  </Typography>
                  
                  <Stack spacing={2}>
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      label="Tuning Requirements"
                      value={pianoTuning.requirements}
                      onChange={(e) => setPianoTuning(prev => ({ 
                        ...prev, 
                        requirements: e.target.value 
                      }))}
                      placeholder="e.g., Steinway Grand Piano tuning required 2 hours before performance"
                      size="small"
                    />
                    
                    <TextField
                      fullWidth
                      select
                      label="Tuning Status"
                      value={pianoTuning.status}
                      onChange={(e) => setPianoTuning(prev => ({ 
                        ...prev, 
                        status: e.target.value 
                      }))}
                      SelectProps={{ native: true }}
                      size="small"
                    >
                      <option value="not_required">Not Required</option>
                      <option value="required">Required</option>
                      <option value="pending">Pending</option>
                      <option value="confirmed">Confirmed</option>
                      <option value="completed">Completed</option>
                    </TextField>
                  </Stack>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" gutterBottom>
                    Engineering Requirements
                  </Typography>
                  
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Engineer Requirements"
                    value={engineerRequirements}
                    onChange={(e) => setEngineerRequirements(e.target.value)}
                    placeholder="e.g., Sound engineer required for soundcheck, Front of house engineer needed"
                    size="small"
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={includesChannelList}
                        onChange={(e) => setIncludesChannelList(e.target.checked)}
                      />
                    }
                    label="Channel list included in tech specs"
                  />
                </Grid>
              </Grid>
              
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  onClick={updateTechnicalRequirements}
                  startIcon={<CheckCircle />}
                >
                  Save Requirements
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Enhanced File Upload Dialog */}
      <Dialog 
        open={uploadDialog} 
        onClose={() => setUploadDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Upload {uploadType === 'schedule' ? 'Schedule' : 
                  uploadType === 'signed_contract' ? 'Signed Contract' : 
                  'Technical Document'}
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              border: '2px dashed #ccc',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              mt: 2,
              cursor: 'pointer',
              transition: 'all 0.2s ease-in-out',
              '&:hover': { 
                borderColor: 'primary.main',
                backgroundColor: 'action.hover'
              }
            }}
            onClick={() => document.getElementById('file-upload')?.click()}
            onDragEnter={(e) => {
              e.preventDefault();
              e.currentTarget.style.borderColor = theme.palette.primary.main;
              e.currentTarget.style.backgroundColor = theme.palette.action.hover;
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              e.currentTarget.style.borderColor = '#ccc';
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              e.currentTarget.style.borderColor = '#ccc';
              e.currentTarget.style.backgroundColor = 'transparent';
              
              const files = Array.from(e.dataTransfer.files);
              const file = files[0];
              if (file && uploadType) {
                handleFileUpload(file, uploadType);
              }
            }}
          >
            <CloudUpload sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography variant="h6" gutterBottom>
              Drop your file here
            </Typography>
            <Typography variant="body1" gutterBottom>
              or click to select file
            </Typography>
            <Typography variant="body2" color="text.secondary">
              PDF, DOC, DOCX files supported (Max 50MB)
            </Typography>
            <input
              id="file-upload"
              type="file"
              accept=".pdf,.doc,.docx"
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file && uploadType) {
                  handleFileUpload(file, uploadType);
                }
              }}
            />
          </Box>
          
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
            Files will be stored in: resources/{resource.performances?.[0]?._id || 'unknown'}/{uploadType === 'schedule' ? 'schedules' : 'technical'}/
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialog(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      <Dialog
  open={!!fileToDelete}
  onClose={() => setFileToDelete(null)}
>
  <DialogTitle>Confirm Delete</DialogTitle>
  <DialogContent>
    <Typography>
      Are you sure you want to delete{' '}
      <strong>{fileToDelete?.file.name}</strong>?
    </Typography>
  </DialogContent>
  <DialogActions>
    <Button onClick={() => setFileToDelete(null)}>Cancel</Button>
    <Button
      color="error"
      onClick={async () => {
        try {
          await axiosinstance.delete(`/api/resources/${resource._id}/files`, {
            data: {
              url: fileToDelete!.file.url,
              category: fileToDelete!.category
            }
          });
          showNotification('File deleted', 'success');
          setFileToDelete(null);
          await fetchFiles();
        } catch (err) {
          console.error('Delete failed:', err);
          showNotification('Failed to delete file', 'error');
        }
      }}
    >
      Delete
    </Button>
  </DialogActions>
</Dialog>
    </Box>
  );
};

export default TechnicalPhase;